import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { usePagination } from "@components/index";
import { fetchClientsList } from "@actions/fetchClientsAction";

export const clientStatusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "deleted", label: "Deleted" },
];

export const searchOptions = [
  {
    value: "global",
    label: "Search All Fields",
    placeholder: "Search by Client Name or Client ID",
  },
];

export const useClientManagementData = () => {
  const [openAddNewClientModal, setOpenAddNewClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [currentClientList, setCurrentClientList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    searchValue: "",
    status: "active",
  });
  const isInitialMount = useRef(true);

  // Load clients data
  const loadClientsData = useCallback(async (status = null) => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchClientsList(status);

      if (result.success) {
        setCurrentClientList(result.data || []);
      } else {
        setError(result.message);
        setCurrentClientList([]);
      }
    } catch (err) {
      setError("Failed to fetch clients");
      setCurrentClientList([]);
      console.error("Error fetching clients:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Combined effect for initial load and filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      // Initial load - fetch all clients
      isInitialMount.current = false;
      loadClientsData(filters.status);
    } else if (filters.status) {
      // Filter changed - fetch filtered clients
      loadClientsData(filters.status);
    }
  }, [filters.status, loadClientsData]);

  // Filter clients based on current filters (client-side filtering for search)
  const filteredClientList = useMemo(() => {
    if (!filters.searchValue) {
      return currentClientList;
    }

    return currentClientList.filter((client) => {
      const searchLower = filters.searchValue.toLowerCase();

      // Global search across client name, created by name/email
      return (
        client.client_name?.toLowerCase().includes(searchLower) ||
        client.client_id?.toString().includes(filters.searchValue)
      );
    });
  }, [currentClientList, filters.searchValue]);

  const handleAddNewClient = () => {
    setEditingClient(null);
    setOpenAddNewClientModal(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setOpenAddNewClientModal(true);
  };

  const handleCloseModal = () => {
    setOpenAddNewClientModal(false);
    setEditingClient(null);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      searchValue: "",
      status: "active",
    });
    loadClientsData("active");
  };

  // Pagination functionality
  const {
    currentPage,
    currentData: paginatedClientList,
    handlePageChange,
    totalPages,
  } = usePagination(filteredClientList, 10);

  return {
    handleAddNewClient,
    handleEditClient,
    handleCloseModal,
    openAddNewClientModal,
    setOpenAddNewClientModal,
    editingClient,
    currentClientList: paginatedClientList,
    allClientList: filteredClientList,
    filters,
    handleFilterChange,
    handleClearFilters,
    currentPage,
    handlePageChange,
    totalPages,
    loading,
    error,
    loadClientsData,
  };
};
